<template>
  <v-navigation-drawer
    v-model="internalValue"
    data-locator="search-page-filters-pane"
    :scrim="true"
    location="right"
    temporary
    :width="480"
  >
    <div class="d-flex flex-column align-center h-100">
      <div class="d-flex align-center py-4 px-6 w-100">
        <h2 class="text-grey-900 text--large">
          {{ $t('common.filters') }}
        </h2>
        <app-button
          class="ml-auto mr-2"
          data-locator="close-button"
          type="icon"
          size="small"
          @click="internalValue = false"
        >
          <app-icon-ds
            icon="fa-xmark"
            size="xs"
            color="grey-600"
          />
        </app-button>
      </div>

      <v-divider class="w-100" />

      <div class="py-4 px-6 h-100 w-100">
        <AppAggregateFormBuilder
          :formData="searchPageFilters"
          :aggregate-scheme="searchPageResultsAggregations"
          @update:fieldValue="handleFieldValueUpdate($event)"
          @submit="handleFiltersSubmit($event)"
          @clear="handleFiltersClear"
        >
          <template #buttons="{ submit, clear }">
            <div class="mt-auto">
              <v-divider class="w-100" />
              <div class="d-flex justify-end ga-4 mt-6 mb-2">
                <app-button
                  type="outlined"
                  variant="neutral"
                  @click="clear()"
                >
                  {{ $t('common.clear') }}
                </app-button>
                <app-button @click="submit()">{{ $t('views.DocumentSearchPage.applyFilters') }}</app-button>
              </div>
            </div>
          </template>
        </AppAggregateFormBuilder>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useStore } from "vuex"

import AppAggregateFormBuilder from "@/common/form-builder/AppAggregateFormBuilder.vue"
import { initFormData } from "@/common/form-builder/formTypes";
import AppButton from "@/design-system/buttons/AppButton.vue"
import { SET_SEARCH_PAGE_FILTERS } from "@/store/modules/documents/mutation_types";


const store = useStore()
const searchPageFilters = computed(() => store.state.documents.searchPageFilters)
const searchPageResultsAggregations = ref(store.state.documents.searchPageResultsAggregations)

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  lastNewQuery: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue', 'update:filters'])

const internalValue = computed({
  get: () => props.modelValue,
  set: (val) => {
    emit('update:modelValue', val)
  },
})

watch(
  () => props.lastNewQuery,
  () => {
    searchPageResultsAggregations.value = store.state.documents.searchPageResultsAggregations
  },
)

watch(
  () => searchPageResultsAggregations.value,
  (aggregateScheme) => {
    store.commit(`documents/${SET_SEARCH_PAGE_FILTERS}`, initFormData(aggregateScheme))
  },
  { deep: true, immediate: true },
)

const handleFiltersSubmit = (formData: Record<string, any>) => {
  emit('update:filters', formData)
  internalValue.value = false
}

const handleFieldValueUpdate = ({ key, value }: { key: string, value: any }) => {
  const updatedFormData = {
    ...searchPageFilters.value,
    [key]: value,
  }

  store.commit(`documents/${SET_SEARCH_PAGE_FILTERS}`, updatedFormData)
}

const handleFiltersClear = () => {
  store.commit(`documents/${SET_SEARCH_PAGE_FILTERS}`, initFormData(searchPageResultsAggregations.value))
}

</script>
